import React, { useState, useEffect } from "react";
import {
  HStack,
  Box,
  Stack,
  VStack,
  Text,
  Button,
  Input,
  Radio,
  Divider,
  Pressable,
} from "native-base";
import { TrashIcon } from "../../../assets/icons/icon-assets";
import { useWindowDimension } from "../../../Utility/WindowDimensions";
import { Loc } from "../../../App/Loc";
import AutoTranslation from "../../../components/header/AccountSettings/AutoTranslation";
import * as AV from "../../../AVCore/AVCore";
import { AVBase } from "../../../AVCore/AVBase";
import { RootStateOrAny, useDispatch, useSelector } from "react-redux";
import { setConvo } from "../../../Redux/Actions/convoActions";
import ProfilePic from "../../../components/common/ProfilePicture";
import Recipients from "../../chat/recipients/Recipients";

const ManageConvoPopup = ({
  onClose,
  convo,
  currentUser,
  toFK,
  setToFK,
  userConvo,
  setConvos,
  setShowConvoListModal,
  setMdlConvoVisible,
  setMdlCreateConvoVisible,
  setConvoLoading,
  setIsOpenConvo,
}) => {
  const dimensions = useWindowDimension();
  const isMobile = dimensions?.width <= 500 || false;
  const [subject, setSubject] = useState(convo?.subject || "");
  const [enabled, setEnabled] = useState(convo?.enabled);
  const [isLoading, setIsLoading] = useState(false);
  const dispatch = useDispatch();
  const [showRecipientsPopup, setShowRecipientsPopup] = useState(false);
  const [recipientCount, setRecipientCount] = useState(
    convo?.people?.length || 0,
  );
  //convo?.people?.filter((x) => x?.id !== currentUser?.id)?.length || 0;
  const [addedPeople, setAddedPeople] = useState<Person[]>([]);
  const [removedPeople, setRemovedPeople] = useState<Person[]>([]);
  const entityDataList = useSelector(
    (state: RootStateOrAny) => state.user.entitydatalist,
  );
  const [sharedPlan, setSharedPlan] = React.useState<AV.ISharedPlan>();

  interface Person {
    id: string;
    title: string;
    thumb?: string;
    type?: string;
  }

  useEffect(() => {
    // Initialize subject when convo changes
    if (convo?.subject) {
      setSubject(convo.subject);
    }

    // Initialize toFK with current people and reset tracking arrays
    if (convo?.people) {
      const updatedPeople = [
        ...convo.people.filter((p) => p?.id !== currentUser?.id),
        convo.people.find((p) => p?.id === currentUser?.id),
      ].filter(Boolean); // remove undefined if user is not found

      setToFK(updatedPeople);
      setAddedPeople([]);
      setRemovedPeople([]);
    }
  }, [convo]);

  useEffect(() => {
    let isMounted = true;

    const loadPlan = async () => {
      try {
        const sharedPlan: AV.ISharedPlan = await AV.AVEntity.getSharedPlan(
          AV.AVEntity.getFk(currentUser),
        );
        if (isMounted) {
          setSharedPlan(sharedPlan);
        }
      } catch (error) {
        if (isMounted) console.error("Failed to load sharedPlan:", error);
      }
    };

    loadPlan();

    return () => {
      isMounted = false;
    };
  }, [currentUser]);

  const handleUpdate = async () => {
    try {
      setIsLoading(true);
      let updateConvo: any;

      // Handle people removal
      if (removedPeople?.length > 0) {
        const returnvalue = await AV.Convo.removePeople(convo, removedPeople);
        if (returnvalue) {
          // Update local state with removed people
          const updatedConvo = {
            ...convo,
            people: convo.people.filter(
              (person) => !removedPeople.some((rp) => rp.id === person.id),
            ),
          };
        }
      }

      // Handle adding new people
      if (addedPeople?.length > 0) {
        const returnvalue = await AV.Convo.addPeople(convo, addedPeople);
        if (returnvalue) {
          // Update local state with new people
          const updatedConvo = {
            ...convo,
            people: [...convo.people, ...addedPeople],
          };
        }
      }

      convo.enabled = enabled;
      convo.subject = convo?.subject !== subject ? subject : convo.subject;
      const convoFk: any = AVBase.getFk(convo);
      convoFk.enabled = enabled;
      convoFk.subject = convo?.subject !== subject ? subject : convo.subject;
      updateConvo = await AV.Convo.update(convoFk);

      if (updateConvo) {
        const updatedConvo = {
          ...convo,
          subject: subject,
          enabled: convoFk.enabled,
          posts: (convo as any).children ?? (convo as any).posts,
        };

        // Update the conversation in Redux store
        const updatedUserConvo = userConvo.map((conv) =>
          conv.id === updatedConvo.id ? updatedConvo : conv,
        );
        dispatch(setConvo(updatedUserConvo));
        setConvos(updatedConvo);
      }

      // Reset tracking arrays after successful update
      setAddedPeople([]);
      setRemovedPeople([]);
      onClose();
    } catch (error) {
      console.error("Error updating conversation:", error);
    } finally {
      setIsLoading(false);
    }
  };

  // RecipientsPopup component
  const RecipientsPopup = () => {
    interface Follow {
      id: string;
      title: string;
      type?: string;
      thumb?: string;
    }

    const [searchText, setSearchText] = useState("");
    const [searchResults, setSearchResults] = useState<Follow[]>([]);
    const [localToFK, setLocalToFK] = useState<Follow[]>(() => {
      // Initialize localToFK with current recipients minus the removed ones plus the added ones
      const currentRecipients = toFK.filter(
        (person) => !removedPeople.some((rp) => rp.id === person.id),
      );
      return [...currentRecipients, ...addedPeople];
    });

    // Search through follows
    useEffect(() => {
      if (!currentUser?.follows || !searchText.trim()) {
        setSearchResults([]);
        return;
      }

      const results = currentUser.follows.filter(
        (follow) =>
          follow.type === "Person" &&
          follow.title?.toLowerCase().includes(searchText.toLowerCase()) &&
          !localToFK.some((recipient) => recipient.id === follow.id),
      );

      setSearchResults(results);
    }, [searchText, currentUser?.follows, localToFK]);

    // Update localToFK when addedPeople or removedPeople change
    useEffect(() => {
      const currentRecipients = toFK.filter(
        (person) => !removedPeople.some((rp) => rp.id === person.id),
      );
      setLocalToFK([...currentRecipients, ...addedPeople]);
    }, [addedPeople, removedPeople, toFK]);

    const handleSelectFollow = (follow: Follow) => {
      // Remove from removedPeople if it was previously removed
      setRemovedPeople((prev) => prev.filter((p) => p.id !== follow.id));

      // Add to addedPeople if not in original toFK
      if (!toFK.some((p) => p.id === follow.id)) {
        setAddedPeople((prev) => [...prev, follow]);
      }

      setSearchText("");
    };

    const handleRemovePerson = (person: Person) => {
      // If person was in addedPeople, just remove from there
      if (addedPeople.some((p) => p.id === person.id)) {
        setAddedPeople((prev) => prev.filter((p) => p.id !== person.id));
      } else {
        // If person was in original toFK, add to removedPeople
        if (toFK.some((p) => p.id === person.id)) {
          setRemovedPeople((prev) => [...prev, person]);
        }
      }
    };

    const handleBack = () => {
      //setToFK(localToFK);
      setShowRecipientsPopup(false);

      const removedIds = new Set(removedPeople.map((person) => person.id));
      const filteredToFK = toFK.filter((person) => !removedIds.has(person.id));
      const extraAddedPeople = addedPeople.filter(
        (person) => !filteredToFK.some((p) => p.id === person.id),
      );

      const uniquePeople = [...filteredToFK, ...extraAddedPeople];
      setRecipientCount(uniquePeople.length);
    };

    return (
      <VStack h="100%" w="100%" bg="white" space={0}>
        {/* Header */}
        <HStack
          justifyContent="space-between"
          p={4}
          alignItems="center"
          borderBottomWidth={1}
          borderBottomColor="gray.200"
        >
          <Text fontSize="xl" fontWeight="semibold">
            {Loc.currentLang.manageConversation.recipients}
          </Text>
        </HStack>

        {/* Search Input */}
        <Box px={4} py={3} bg="gray.50">
          <Input
            placeholder={Loc.currentLang.manageConversation.searchRecipients}
            value={searchText}
            onChangeText={setSearchText}
            borderRadius="full"
            bg="white"
            borderColor="gray.200"
            _focus={{ borderColor: "#00A7BC" }}
            fontSize="md"
          />
        </Box>

        {/* Search Results and Current Recipients */}
        <Box flex={1} px={4} py={2}>
          <VStack space={3}>
            {/* Search Results */}
            {searchText.trim() &&
              searchResults.map((follow) => (
                <Pressable
                  key={follow.id}
                  onPress={() => handleSelectFollow(follow)}
                  py={2}
                >
                  <HStack space={3} alignItems="center">
                    <Box
                      size={10}
                      borderRadius="full"
                      bg="gray.100"
                      overflow="hidden"
                    >
                      <ProfilePic
                        width={38}
                        height={38}
                        link={
                          currentUser?.id == follow?.id
                            ? AV.AVEntity.getLink(currentUser)
                            : entityDataList?.find(
                                  (x: any) => x?.id == follow?.id,
                                )
                              ? AV.AVEntity.getLink(
                                  entityDataList?.find(
                                    (x: any) => x?.id == follow?.id,
                                  ),
                                )
                              : follow
                        }
                      />
                    </Box>
                    <Text fontSize="md">{follow.title}</Text>
                  </HStack>
                </Pressable>
              ))}

            {/* Current Recipients */}
            {!searchText.trim() &&
              localToFK.map((person) => (
                <HStack
                  key={person.id}
                  justifyContent="space-between"
                  alignItems="center"
                  py={2}
                >
                  <HStack space={3} alignItems="center">
                    <Box
                      size={10}
                      borderRadius="full"
                      bg="gray.100"
                      overflow="hidden"
                    >
                      <ProfilePic
                        width={38}
                        height={38}
                        link={
                          currentUser?.id == person?.id
                            ? AV.AVEntity.getLink(currentUser)
                            : entityDataList?.find(
                                  (x: any) => x?.id == person?.id,
                                )
                              ? AV.AVEntity.getLink(
                                  entityDataList?.find(
                                    (x: any) => x?.id == person?.id,
                                  ),
                                )
                              : person
                        }
                      />
                    </Box>
                    <Text fontSize="md">{person.title}</Text>
                  </HStack>

                  {person?.id !== currentUser?.id && (
                    <Pressable
                      onPress={() => handleRemovePerson(person)}
                      hitSlop={8}
                    >
                      <Box p={1}>
                        <TrashIcon height="16" width="16" />
                      </Box>
                    </Pressable>
                  )}
                </HStack>
              ))}
          </VStack>
        </Box>

        {/* Footer */}
        <HStack
          p={4}
          borderTopWidth={1}
          borderTopColor="gray.100"
          justifyContent="center"
          bg="gray.50"
        >
          {/*<Button*/}
          {/*  rounded="full"*/}
          {/*  height="40px"*/}
          {/*  width="100px"*/}
          {/*  backgroundColor="gray.300"*/}
          {/*  onPress={() => setShowRecipientsPopup(false)}*/}
          {/*>*/}
          {/*  <Text color="black">{Loc.currentLang.menu.cancel}</Text>*/}
          {/*</Button>*/}
          <Button
            rounded="full"
            height="40px"
            width="100px"
            backgroundColor="AVColor.primary"
            onPress={handleBack}
          >
            {Loc.currentLang.manageConversation.back}
          </Button>
        </HStack>
      </VStack>
    );
  };

  return (
    <Box
      position="fixed"
      top="0"
      left="0"
      right="0"
      bottom="0"
      bg="rgba(0, 0, 0, 0.5)"
      zIndex={1000}
      display="flex"
      justifyContent="center"
      alignItems="center"
    >
      {/* Main Dialog */}
      <Box
        width={isMobile ? `${dimensions.width - 15}px` : "600px"}
        bg="white"
        borderRadius="8px"
        shadow={2}
        display="flex"
        flexDirection="column"
      >
        {/* Header */}
        <HStack
          justifyContent="space-between"
          alignItems="center"
          px={4}
          py={3}
          borderBottomWidth="2px"
          borderBottomColor="gray.100"
        >
          <Text fontSize="20px" fontWeight="600">
            {Loc.currentLang.manageConversation.manageConversation}
          </Text>
        </HStack>

        {/* Content */}
        <Box flex={1} overflowY="auto">
          <VStack p={5} space={4}>
            {convo?.subject?.trim()?.length >= 1 && (
              <>
                {/* Recipients */}
                <HStack justifyContent="space-between" alignItems="center">
                  <Text fontSize="16px" fontWeight="500">
                    {Loc.currentLang.manageConversation.recipients}
                  </Text>
                  <Pressable onPress={() => setShowRecipientsPopup(true)}>
                    <Text color="#00A7BC" fontSize="14px">
                      {`${recipientCount}+ ${Loc.currentLang.manageConversation.recipients}`}
                    </Text>
                  </Pressable>
                </HStack>

                {/* Recipients Popup Overlay */}
                {showRecipientsPopup && (
                  <Box
                    position="fixed"
                    top={0}
                    left={0}
                    right={0}
                    bottom={0}
                    bg="rgba(0, 0, 0, 0.7)"
                    zIndex={2000}
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                  >
                    <Box
                      bg="white"
                      borderRadius="8px"
                      shadow="lg"
                      width={isMobile ? "90%" : "400px"}
                      maxHeight="90vh"
                      overflow="hidden"
                    >
                      <Recipients
                        removedPeople={removedPeople}
                        addedPeople={addedPeople}
                        toFK={toFK}
                        setRemovedPeople={setRemovedPeople}
                        setAddedPeople={setAddedPeople}
                        setShowRecipientsPopup={setShowRecipientsPopup}
                        setRecipientCount={setRecipientCount}
                        setShowConvoListModal={setShowConvoListModal}
                        setMdlConvoVisible={setMdlConvoVisible}
                        setMdlCreateConvoVisible={setMdlCreateConvoVisible}
                        setConvoLoading={setConvoLoading}
                        setIsOpenConvo={setIsOpenConvo}
                      />
                    </Box>
                  </Box>
                )}
                <Divider
                  marginY={"10px"}
                  marginLeft={"3px"}
                  width={"100%"}
                  zIndex={-1}
                />

                {/* Subject */}
                <HStack
                  justifyContent="space-between"
                  alignItems="center"
                  space={4}
                >
                  <Text fontSize="16px" fontWeight="500">
                    {Loc.currentLang.menu.subject}
                  </Text>
                  <Input
                    flex={1}
                    value={subject}
                    onChangeText={(text) => setSubject(text)}
                    placeholder={Loc.currentLang.menu.enterSubject}
                    borderRadius="4px"
                    borderColor="gray.300"
                    _focus={{
                      borderColor: "#00A7BC",
                    }}
                    height="40px"
                  />
                </HStack>

                {/* Enabled */}
                <HStack justifyContent="space-between" alignItems="center">
                  <Text fontSize="16px" fontWeight="500">
                    {Loc.currentLang.manageConversation.enabled}
                  </Text>
                  <Radio.Group
                    name={Loc.currentLang.manageConversation.enabled}
                    value={enabled ? "true" : "false"}
                    onChange={(value) => setEnabled(value === "true")}
                  >
                    <Stack
                      direction={{
                        base: "row",
                        md: "row",
                      }}
                      alignItems={{
                        base: "flex-wrap",
                      }}
                      space={isMobile ? "30px" : "65px"}
                      w="75%"
                      right="170%"
                      maxW="300px"
                    >
                      <Radio value="true">
                        <Text ml={2}>{Loc.currentLang.menu.yes}</Text>
                      </Radio>
                      <Radio value="false">
                        <Text ml={2}>{Loc.currentLang.menu.no}</Text>
                      </Radio>
                    </Stack>
                  </Radio.Group>
                </HStack>
                <Divider
                  marginY={"10px"}
                  marginLeft={"3px"}
                  width={"100%"}
                  zIndex={-1}
                />
              </>
            )}

            {/* Auto Translation */}
            <VStack space={0}>
              <Text fontSize="16px" fontWeight="500" mb={0}>
                {Loc.currentLang.manageConversation.autoTranslation}
              </Text>
              <AutoTranslation
                isAutoTrans={true}
                entity={currentUser}
                sharedPlan={sharedPlan}
                setSharedPlan={setSharedPlan}
                triggerBuyCredits={false}
                setTriggerBuyCredits={() => {}}
              />
            </VStack>
          </VStack>
        </Box>

        {/* Footer */}
        <Box
          mt="auto"
          borderTopWidth="2px"
          borderTopColor="gray.100"
          bg="white"
          position="sticky"
          bottom={0}
          left={0}
          right={0}
          zIndex={showRecipientsPopup ? "-1" : "-9"}
        >
          <HStack justifyContent="flex-end" space={2} p={4}>
            <Button
              rounded="full"
              height="40px"
              width="100px"
              isDisabled={isLoading}
              backgroundColor="gray.300"
              onPress={onClose}
            >
              <Text color="black">{Loc.currentLang.menu.close}</Text>
            </Button>
            {convo?.subject?.trim()?.length >= 1 && (
              <>
                <Button
                  rounded="full"
                  height="40px"
                  width="100px"
                  isLoading={isLoading}
                  isDisabled={isLoading}
                  backgroundColor="AVColor.primary"
                  onPress={handleUpdate}
                >
                  <Text fontSize="16px" color="white">
                    {Loc.currentLang.menu.update}
                  </Text>
                </Button>
              </>
            )}
          </HStack>
        </Box>
      </Box>
    </Box>
  );
};

export default ManageConvoPopup;
